import {
  GlobalSection,
  Website,
  WebsiteData,
  accountSectionTypeToVariantMapping,
  sectionTypeToVariantMapping,
} from '@betterfunnel/types';
import AlertModal from '@betterfunnel/ui-core/AlertModal';
import * as Sentry from '@sentry/react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { uid } from 'uid';
import { mergeImages } from '../components/FormFields/FileUpload/FileUploadInput';
import { db } from '../Firebase';
import { publishGlobalSection } from '../services/GlobalSectionsService';
import { publishWebsite as publishWebsiteApi } from '../services/WebsitesService';
import {
  emptyConverter,
  groupBy,
  sendMessageToIframeByWebsiteId,
} from '../utils';
import { track } from '../utils/analytics';
import { LimitedStack } from '../utils/limited-stack';
import { extractUsedSearchParamsFromSection } from '../utils/quill';
import { useAuditLog } from './AuditLogContext';
import { UserContext } from './UserContext';

export function getItemFromSection(
  section: any,
  propKey?: string,
  subitems?: { id: string; propKey: string; type: 'array' }[] | undefined,
) {
  let itemFound: any = section;

  if (subitems?.length) {
    subitems.forEach((item) => {
      if (item.type === 'array') {
        itemFound = itemFound?.[item.propKey]?.find(
          (it: any) => it.id === item.id,
        );
      } else {
        throw new Error('unknown subitem type: ' + item.type);
      }
    });
    if (!itemFound) return;
  }

  if (!propKey) return itemFound;
  const propKeyParts = propKey.split('.');
  propKeyParts.forEach((propKeyPart) => {
    if (!itemFound) return;

    itemFound = itemFound[propKeyPart];
  });

  return itemFound;
}

export type WebsiteUpdateType =
  | 'text'
  | 'changeDesign'
  | 'addSectionBelow'
  | 'moveSectionUp'
  | 'moveSectionDown'
  | 'reorderSections'
  | 'deleteSection'
  | 'duplicateSection'
  | 'deleteSectionItem'
  | 'editSectionItem'
  | 'duplicateSectionItem'
  | 'addSectionItem'
  | 'editButton'
  | 'editSection'
  | 'editForm'
  | 'editImageSettings'
  | 'selectImage'
  | 'revertChange'
  | 'discard'
  | 'resetConfig'
  | 'resetBrand'
  | 'editBrand'
  | 'editConfig'
  | 'convertGlobalToLocal'
  | 'convertLocalToGlobal'
  | 'customValueMentionClick';

type UpdateWebsiteFn = (wu: WebsiteUpdate) => void;

type CreateUpdateWebsiteFn = (
  dynamicPageId: string,
  websiteId: string,
  sectionId: string,
  propKey: string,
  content: any,
  type: WebsiteUpdateType,
  subitems?: { id: string; propKey: string; type: 'array' }[],
  before?: WebsiteData,
) => WebsiteUpdate | undefined;

export type WebsiteUpdate = {
  id: string;
  accountId: string;
  campaignId: string;
  websiteId: string;
  sectionId: string;
  propKey: string;
  content: any;
  type: WebsiteUpdateType;
  subitems?: { id: string; propKey: string; type: 'array' }[];
  before?: WebsiteData;
};

interface WebsiteUpdaterContextProps {
  saveStatus: 'none' | 'saving' | 'done';
  setSaveStatus: (_: 'none' | 'saving' | 'done') => void;

  publishing: boolean;
  publish: (dynamicPageId: string, websiteId: string) => Promise<any>;

  discarding: boolean;
  discardChanges: (dynamicPageId: string, websiteId: string) => Promise<any>;

  createWebsiteUpdate: CreateUpdateWebsiteFn;
  updateWebsite: UpdateWebsiteFn;

  canRevert: boolean;
  canForward: boolean;
  revertLatestUpdate: () => boolean;
  forwardRevert: () => boolean;
  clearHistory: () => void;

  applyChangesSync: (
    websiteId: string,
    development: WebsiteData,
    extra?: WebsiteUpdate[],
  ) => any;
}

export const WebsiteUpdaterContext = createContext<WebsiteUpdaterContextProps>({
  saveStatus: 'none',
  setSaveStatus: () => {},

  publishing: false,
  publish: async () => {},

  discarding: false,
  discardChanges: async () => {},

  updateWebsite: () => undefined,
  createWebsiteUpdate: () => undefined,

  canRevert: false,
  canForward: false,
  revertLatestUpdate: () => false,
  forwardRevert: () => false,
  clearHistory: () => {},

  applyChangesSync: () => {},
});

export function WebsiteUpdaterContextProvider(props: any) {
  const { selectedAccount } = useContext(UserContext);
  const auditLog = useAuditLog();
  const [saveStatus, setSaveStatus] = useState<'none' | 'saving' | 'done'>(
    'none',
  );
  const [publishing, setPublishing] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [updates, setUpdates] = useState<WebsiteUpdate[]>([]);
  const [history, setHistory] = useState<LimitedStack<WebsiteUpdate>>(
    new LimitedStack(20),
  );
  const [revertHistory, setRevertHistory] = useState<
    LimitedStack<WebsiteUpdate>
  >(new LimitedStack(20));
  const [publishGlobalSectionModalOpen, setPublishGlobalSectionModalOpen] =
    useState<{
      websiteId: string;
      globalSectionId: string;
      loading?: boolean;
    } | null>(null);

  const createWebsiteUpdate: CreateUpdateWebsiteFn = useCallback(
    (
      dynamicPageId,
      websiteId,
      sectionId,
      propKey,
      content,
      type,
      subitems,
      before,
    ) => {
      if (!selectedAccount) return;

      const wu: WebsiteUpdate = {
        id: uid(8),
        accountId: selectedAccount.id,
        campaignId: dynamicPageId,
        websiteId,
        sectionId,
        propKey,
        subitems,
        content,
        type,
        before,
      };

      return wu;
    },
    [selectedAccount],
  );

  const updateWebsite: UpdateWebsiteFn = useCallback(
    (wu: WebsiteUpdate) => {
      if (!selectedAccount) return;

      setSaveStatus('saving');
      setUpdates((u) => [...u, wu]);
      if (wu.before) {
        setHistory((h) => {
          const newHistory = new LimitedStack(h);
          newHistory.push(wu);
          return newHistory;
        });
      }
      setRevertHistory(new LimitedStack(20));
    },
    [selectedAccount],
  );

  const clearHistory = useCallback(() => {
    setHistory((history) => new LimitedStack(history.getMaxLength()));
    setRevertHistory(
      (revertHistory) => new LimitedStack(revertHistory.getMaxLength()),
    );
  }, []);

  const applyTextChange = (wu: WebsiteUpdate, development: WebsiteData) => {
    const section =
      development.sections?.find((s: any) => s.id === wu.sectionId) ??
      development.banners?.find((s: any) => s.id === wu.sectionId);
    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    let itemFound: any = section;

    if (wu.subitems?.length) {
      wu.subitems.forEach((item) => {
        if (item.type === 'array') {
          itemFound = itemFound?.[item.propKey]?.find(
            (it: any) => it.id === item.id,
          );
        } else {
          throw new Error('unknown subitem type: ' + item.type);
        }
      });
      if (!itemFound) return false;
    }
    const propKeyParts = wu.propKey.split('.');
    propKeyParts.forEach((propKeyPart, i) => {
      const isLast = i + 1 === propKeyParts.length;
      if (isLast) {
        itemFound[propKeyPart] = wu.content;
      } else {
        let newItem = itemFound[propKeyPart];
        if (!newItem) newItem = itemFound[propKeyPart] = {};
        itemFound = newItem;
      }
    });

    section.lastUpdatedAt = new Date().toISOString();
    section.usedSearchParams = extractUsedSearchParamsFromSection(section);

    return true;
  };

  const applyMoveSectionChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
    direction: 'up' | 'down',
  ) => {
    const sectionIdx = development.sections?.findIndex(
      (s: any) => s.id === wu.sectionId,
    );
    if (sectionIdx === -1) return false;

    const section = development.sections[sectionIdx];
    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    const idxShift = direction === 'up' ? -1 : 1;
    const newIdx = Math.max(0, sectionIdx + idxShift);
    const newSections = [
      ...development.sections.filter((s: any) => s.id !== wu.sectionId),
    ];
    newSections.splice(newIdx, 0, section);
    development.sections = newSections;

    return true;
  };

  const applyAddSectionChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
    where: 'above' | 'below',
  ) => {
    if (!wu.content) return false;

    const alreadyAddedIdx = development.sections?.findIndex(
      (s: any) => s.id === wu.content.id,
    );

    // If the section is already added, return false
    if (alreadyAddedIdx !== -1) return false;

    let sectionIdx = development.sections?.findIndex(
      (s: any) => s.id === wu.sectionId,
    );
    if (sectionIdx === -1) sectionIdx = development.sections?.length || 0;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, sectionIdx);

    const idxShift = where === 'above' ? -1 : 1;
    const newIdx = Math.max(0, sectionIdx + idxShift);
    const newSections = [...development.sections];
    newSections.splice(newIdx, 0, wu.content);
    development.sections = newSections;

    return true;
  };

  const applyReorderSectionsChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    if (!wu.content || !Array.isArray(wu.content)) return false;

    const sectionsOrderMap: Record<string, number> = wu.content.reduce(
      (acc, section, idx) => {
        acc[section.id] = idx;
        return acc;
      },
      {} as Record<string, number>,
    );

    const currentSectionsOrderMap: Record<string, number> =
      development.sections.reduce(
        (acc, section, idx) => {
          acc[section.id] = idx;
          return acc;
        },
        {} as Record<string, number>,
      );

    console.log('[WEBSITE_UPDATER]', wu.type, wu, wu.content);

    const newSections = [...development.sections].sort((a, b) => {
      const aOrder =
        sectionsOrderMap[a.id] ?? currentSectionsOrderMap[a.id] ?? 0;
      const bOrder =
        sectionsOrderMap[b.id] ?? currentSectionsOrderMap[b.id] ?? 0;
      return aOrder - bOrder;
    });
    development.sections = newSections;

    return true;
  };

  const applyDeleteSectionChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    development.sections = development.sections?.filter(
      (s: any) => s.id !== wu.sectionId,
    );
    development.banners =
      development.banners?.filter((s: any) => s.id !== wu.sectionId) ?? [];

    return true;
  };

  const applyDuplicateSectionChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    const sectionIdx = development.sections?.findIndex(
      (s: any) => s.id === wu.sectionId,
    );
    if (sectionIdx === -1) return false;

    const section = development.sections[sectionIdx];
    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    const newId = wu.id || uid(8);

    if (!development.sections.find((s) => s.id === newId)) {
      const newSections = [...development.sections];
      newSections.splice(sectionIdx + 1, 0, {
        ...JSON.parse(JSON.stringify(section)),
        id: uid(8),
      });
      development.sections = newSections;
    }

    return true;
  };

  const applyDesignChange = (wu: WebsiteUpdate, development: WebsiteData) => {
    const section =
      development.sections?.find((s: any) => s.id === wu.sectionId) ??
      development.banners?.find((s: any) => s.id === wu.sectionId);

    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    if (wu.content) {
      // apply all section in content to the section
      Object.entries(wu.content).forEach(([key, value]) => {
        const _key: keyof typeof section = key as any;
        if (_key === 'id') return;
        section[_key] = value;
      });
    } else {
      // Switch between variants one by one
      const currentVariant = section.variant;
      const allVariants = sectionTypeToVariantMapping[section.type]?.concat(
        (accountSectionTypeToVariantMapping[wu.accountId]?.[section.type] ??
          []) as any,
      );
      const relevantVariants = allVariants.filter((v) =>
        section.type !== 'hero'
          ? true
          : development.formIds?.length
            ? true
            : v !== 'v6' && v !== 'v7',
      );
      if (!relevantVariants?.length) return false;

      const currentVariantIdx = relevantVariants.indexOf(currentVariant);
      const newVariant =
        relevantVariants[(currentVariantIdx + 1) % relevantVariants.length];

      // const newVariant = getRandomElementWithSeed(wu.id, allVariants);
      section.variant = newVariant as any;
    }

    section.lastUpdatedAt = new Date().toISOString();
    section.usedSearchParams = extractUsedSearchParamsFromSection(section);

    return true;
  };

  const applyAddSectionItemChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    const section =
      development.sections?.find((s: any) => s.id === wu.sectionId) ??
      development.banners?.find((s: any) => s.id === wu.sectionId);
    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    let itemFound: any = section;

    const propKeyParts = wu.propKey.split('.');
    propKeyParts.forEach((propKeyPart, i) => {
      const isLast = i + 1 === propKeyParts.length;
      if (isLast) {
        if (Array.isArray(itemFound[propKeyPart])) {
          const itemToCopy = wu.content || itemFound[propKeyPart][0];
          const newItem = {
            ...(itemToCopy ? JSON.parse(JSON.stringify(itemToCopy)) : {}),
            id: uid(8),
          };
          itemFound[propKeyPart] = [...itemFound[propKeyPart], newItem];
        } else {
          itemFound[propKeyPart] = [{ id: uid(8) }];
        }
      } else {
        let newItem = itemFound[propKeyPart];
        if (!newItem) newItem = itemFound[propKeyPart] = {};
        itemFound = newItem;
      }
    });

    section.lastUpdatedAt = new Date().toISOString();
    section.usedSearchParams = extractUsedSearchParamsFromSection(section);

    return true;
  };

  const applyDeleteSectionItemChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    const section =
      development.sections?.find((s: any) => s.id === wu.sectionId) ??
      development.banners?.find((s: any) => s.id === wu.sectionId);
    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    let itemFound: any = section;

    const propKeyParts = wu.propKey.split('.');
    propKeyParts.forEach((propKeyPart, i) => {
      if (!itemFound) return;

      const isLast = i + 1 === propKeyParts.length;
      if (isLast) {
        if (
          Array.isArray(itemFound[propKeyPart]) &&
          itemFound[propKeyPart].length
        ) {
          itemFound[propKeyPart] = itemFound[propKeyPart]?.filter(
            (item: any) => item.id !== wu.content,
          );
        }
      } else {
        itemFound = itemFound[propKeyPart];
      }
    });

    section.lastUpdatedAt = new Date().toISOString();
    section.usedSearchParams = extractUsedSearchParamsFromSection(section);

    return true;
  };

  const applyEditSectionItemChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    const section =
      development.sections?.find((s: any) => s.id === wu.sectionId) ??
      development.banners?.find((s: any) => s.id === wu.sectionId);
    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    let itemFound: any = section;

    const propKeyParts = wu.propKey.split('.');
    propKeyParts.forEach((propKeyPart, i) => {
      if (!itemFound) return;

      const isLast = i + 1 === propKeyParts.length;
      if (isLast) {
        if (
          Array.isArray(itemFound[propKeyPart]) &&
          itemFound[propKeyPart].length
        ) {
          const itemIndex = itemFound[propKeyPart].findIndex(
            (item: any) => item.id === wu.content?.id,
          );
          if (itemIndex !== -1) {
            itemFound[propKeyPart][itemIndex] =
              wu.content?.data || itemFound[propKeyPart][itemIndex];
          }
        }
      } else {
        itemFound = itemFound[propKeyPart];
      }
    });

    section.lastUpdatedAt = new Date().toISOString();
    section.usedSearchParams = extractUsedSearchParamsFromSection(section);

    return true;
  };

  const applyEditButtonChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    const section =
      development.sections?.find((s: any) => s.id === wu.sectionId) ??
      development.banners?.find((s: any) => s.id === wu.sectionId);
    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    let itemFound: any = section;

    if (wu.subitems?.length) {
      wu.subitems.forEach((item, index) => {
        const isLast = index + 1 === wu.subitems?.length;

        if (item.type === 'array') {
          if (isLast) {
            const itemFoundIndex = itemFound?.[item.propKey]?.findIndex(
              (it: any) => it.id === item.id,
            );

            if (itemFoundIndex !== -1 && itemFound?.[item.propKey]) {
              itemFound[item.propKey][itemFoundIndex] = {
                ...itemFound[item.propKey][itemFoundIndex],
                ...wu.content,
              };
            }
          } else {
            itemFound = itemFound?.[item.propKey]?.find(
              (it: any) => it.id === item.id,
            );
          }
        } else {
          throw new Error('unknown subitem type: ' + item.type);
        }
      });
    }

    section.lastUpdatedAt = new Date().toISOString();
    section.usedSearchParams = extractUsedSearchParamsFromSection(section);

    return true;
  };

  const applyEditConfigChange = (
    configType: 'config' | 'brand',
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    console.log('[WEBSITE_UPDATER]', wu.type, configType, wu);

    if (!development[configType]) {
      development[configType] = {};
    }

    development[configType] = {
      ...development[configType],
      ...wu.content,
    };

    development.sections?.forEach((section: any) => {
      section.lastUpdatedAt = new Date().toISOString();
    });

    return true;
  };

  const applyResetConfigChange = (
    configType: 'config' | 'brand',
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    console.log('[WEBSITE_UPDATER]', wu.type, configType, wu);

    development[configType] = wu.content;

    development.sections?.forEach((section: any) => {
      section.lastUpdatedAt = new Date().toISOString();
    });

    return true;
  };

  const applyDuplicateSectionItemChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    const section =
      development.sections?.find((s: any) => s.id === wu.sectionId) ??
      development.banners?.find((s: any) => s.id === wu.sectionId);
    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    let arrayFound: any = section;

    const propKeyParts = wu.propKey.split('.');
    propKeyParts.forEach((propKeyPart) => {
      if (!arrayFound) return;

      arrayFound = arrayFound[propKeyPart];
    });

    if (arrayFound && Array.isArray(arrayFound) && arrayFound.length) {
      const newId = wu.id || uid(8);
      const alreadyDuplicatedIdx = arrayFound.findIndex(
        (item: any) => item.id === newId,
      );
      if (alreadyDuplicatedIdx !== -1) return false;

      const itemIndex = arrayFound.findIndex(
        (item: any) => item.id === wu.content?.id,
      );
      if (itemIndex !== -1) {
        arrayFound.splice(itemIndex + 1, 0, {
          ...arrayFound[itemIndex],
          id: newId,
        });
      }
    }

    section.lastUpdatedAt = new Date().toISOString();
    section.usedSearchParams = extractUsedSearchParamsFromSection(section);

    return true;
  };

  const applySelectImageChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    const section =
      development.sections?.find((s: any) => s.id === wu.sectionId) ??
      development.banners?.find((s: any) => s.id === wu.sectionId);
    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    let itemFound: any = section;
    if (wu.subitems?.length) {
      wu.subitems.forEach((item) => {
        if (item.type === 'array') {
          itemFound = itemFound?.[item.propKey]?.find(
            (it: any) => it.id === item.id,
          );
        } else {
          throw new Error('unknown subitem type: ' + item.type);
        }
      });
      if (!itemFound) return false;
    }
    const propKeyParts = wu.propKey.split('.');
    propKeyParts.forEach((propKeyPart, i) => {
      const isLast = i + 1 === propKeyParts.length;
      if (isLast) {
        itemFound[propKeyPart] = mergeImages(
          wu.content,
          itemFound[propKeyPart],
        );
      } else {
        let newItem = itemFound[propKeyPart];
        if (!newItem) newItem = itemFound[propKeyPart] = {};
        itemFound = newItem;
      }
    });

    section.lastUpdatedAt = new Date().toISOString();
    section.usedSearchParams = extractUsedSearchParamsFromSection(section);

    return true;
  };

  const applyEditSection = (wu: WebsiteUpdate, development: WebsiteData) => {
    const section =
      development.sections?.find((s: any) => s.id === wu.sectionId) ??
      development.banners?.find((s: any) => s.id === wu.sectionId);

    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    Object.assign(section, wu.content);
    section.lastUpdatedAt = new Date().toISOString();
    section.usedSearchParams = extractUsedSearchParamsFromSection(section);

    return true;
  };

  const applyEditImageSettings = (
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    const section =
      development.sections?.find((s: any) => s.id === wu.sectionId) ??
      development.banners?.find((s: any) => s.id === wu.sectionId);

    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    let itemFound: any = section;
    if (wu.subitems?.length) {
      wu.subitems.forEach((item) => {
        if (item.type === 'array') {
          itemFound = itemFound?.[item.propKey]?.find(
            (it: any) => it.id === item.id,
          );
        } else {
          throw new Error('unknown subitem type: ' + item.type);
        }
      });
      if (!itemFound) return false;
    }
    const propKeyParts = wu.propKey.split('.');
    propKeyParts.forEach((propKeyPart, i) => {
      const isLast = i + 1 === propKeyParts.length;
      if (isLast) {
        itemFound[propKeyPart] = {
          ...itemFound[propKeyPart],
          [wu.content.type]: wu.content.value,
        };
      } else {
        let newItem = itemFound[propKeyPart];
        if (!newItem) newItem = itemFound[propKeyPart] = {};
        itemFound = { ...newItem, [wu.content.type]: wu.content.value };
      }
    });

    section.lastUpdatedAt = new Date().toISOString();
    section.usedSearchParams = extractUsedSearchParamsFromSection(section);

    return true;
  };

  const applyConvertGlobalToLocalChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    const section =
      development.sections?.find((s: any) => s.id === wu.sectionId) ??
      development.banners?.find((s: any) => s.id === wu.sectionId);
    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    section.global = null;
    section.lastUpdatedAt = new Date().toISOString();

    return true;
  };

  const applyConvertLocalToGlobalChange = (
    wu: WebsiteUpdate,
    development: WebsiteData,
  ) => {
    const section =
      development.sections?.find((s: any) => s.id === wu.sectionId) ??
      development.banners?.find((s: any) => s.id === wu.sectionId);
    if (!section) return false;

    console.log('[WEBSITE_UPDATER]', wu.type, wu, section);

    section.global = wu.content.global;
    section.lastUpdatedAt = new Date().toISOString();

    return true;
  };

  const applyRevertChange = (wu: WebsiteUpdate, development: WebsiteData) => {
    development.sections = wu.before?.sections || development.sections;

    return true;
  };

  const applyDiscardChange = (wu: WebsiteUpdate, development: WebsiteData) => {
    development.sections = wu.content?.sections || development.sections;

    return true;
  };

  const applyChange = useCallback(
    (wu: WebsiteUpdate, development: WebsiteData) => {
      switch (wu.type) {
        case 'text':
          return applyTextChange(wu, development);

        case 'moveSectionUp':
          return applyMoveSectionChange(wu, development, 'up');

        case 'moveSectionDown':
          return applyMoveSectionChange(wu, development, 'down');

        case 'addSectionBelow':
          return applyAddSectionChange(wu, development, 'below');

        case 'reorderSections':
          return applyReorderSectionsChange(wu, development);

        case 'deleteSection':
          return applyDeleteSectionChange(wu, development);

        case 'duplicateSection':
          return applyDuplicateSectionChange(wu, development);

        case 'addSectionItem':
          return applyAddSectionItemChange(wu, development);

        case 'deleteSectionItem':
          return applyDeleteSectionItemChange(wu, development);

        case 'editSectionItem':
          return applyEditSectionItemChange(wu, development);

        case 'editButton':
          return applyEditButtonChange(wu, development);

        case 'editConfig':
          return applyEditConfigChange('config', wu, development);

        case 'editBrand':
          return applyEditConfigChange('brand', wu, development);

        case 'resetConfig':
          return applyResetConfigChange('config', wu, development);

        case 'resetBrand':
          return applyResetConfigChange('brand', wu, development);

        case 'duplicateSectionItem':
          return applyDuplicateSectionItemChange(wu, development);

        case 'selectImage':
          return applySelectImageChange(wu, development);

        case 'changeDesign':
          return applyDesignChange(wu, development);

        case 'editSection':
          return applyEditSection(wu, development);

        case 'editImageSettings':
          return applyEditImageSettings(wu, development);

        case 'revertChange':
          return applyRevertChange(wu, development);

        case 'discard':
          return applyDiscardChange(wu, development);

        case 'convertGlobalToLocal':
          return applyConvertGlobalToLocalChange(wu, development);

        case 'convertLocalToGlobal':
          return applyConvertLocalToGlobalChange(wu, development);

        default:
          throw new Error('Unable to apply update of type: ' + wu.type);
      }
    },
    [],
  );

  const applyChangesSync = useCallback(
    (websiteId: string, development: WebsiteData, extra?: WebsiteUpdate[]) => {
      const websiteUpdates = [...updates, ...(extra ?? [])].filter(
        (wu) => wu.websiteId === websiteId,
      );

      websiteUpdates.forEach((wu) => applyChange(wu, development));

      return development;
    },
    [applyChange, updates],
  );

  const revertLatestUpdate = useCallback(() => {
    if (!history.getSize()) return false;

    const newHistory = new LimitedStack(history);
    const changeToRevert = newHistory.pop();

    setHistory(newHistory);

    if (!changeToRevert?.before) return false;

    const revertWebsiteUpdate = createWebsiteUpdate(
      changeToRevert.campaignId,
      changeToRevert.websiteId,
      changeToRevert.sectionId,
      changeToRevert.propKey,
      changeToRevert.content,
      'revertChange',
      changeToRevert.subitems,
      changeToRevert.before,
    );

    if (!revertWebsiteUpdate) return false;

    setSaveStatus('saving');
    setUpdates((u) => [...u, revertWebsiteUpdate]);
    setRevertHistory((rh) => {
      const newHistory = new LimitedStack(rh);
      newHistory.push({ ...changeToRevert, id: uid(8) });
      return newHistory;
    });

    const newDev = JSON.parse(JSON.stringify(changeToRevert.before));
    applyChange(revertWebsiteUpdate, newDev);
    sendMessageToIframeByWebsiteId(
      {
        eventName: 'websiteUpdated',
        website: newDev,
      },
      changeToRevert.websiteId,
    );

    return true;
  }, [history, createWebsiteUpdate, applyChange]);

  const forwardRevert = useCallback(() => {
    if (!revertHistory.getSize()) return false;

    const newHistory = new LimitedStack(revertHistory);
    const forwardChange = newHistory.pop();

    setRevertHistory(newHistory);

    if (!forwardChange) return false;

    setSaveStatus('saving');
    setUpdates((u) => [...u, forwardChange]);
    setHistory((h) => {
      const newHistory = new LimitedStack(h);
      newHistory.push(forwardChange);
      return newHistory;
    });

    const newDev = JSON.parse(JSON.stringify(forwardChange.before));
    applyChange(forwardChange, newDev);
    sendMessageToIframeByWebsiteId(
      {
        eventName: 'websiteUpdated',
        website: newDev,
      },
      forwardChange.websiteId,
    );

    return true;
  }, [applyChange, revertHistory]);

  const _saveWebsite = useCallback(
    async (
      websiteId: string,
      websiteUpdates: WebsiteUpdate[],
      dynamicPageId: string,
    ) => {
      const websiteDoc = await getDoc(
        doc(
          db,
          'accounts',
          websiteUpdates[0].accountId,
          'campaigns',
          dynamicPageId,
          'websites',
          websiteId,
        ).withConverter(emptyConverter<Website>()),
      );

      const development = websiteDoc.data()?.development;
      if (!websiteDoc.exists() || !development) return { updated: false };

      let update = false;

      for (const wu of websiteUpdates) {
        update = applyChange(wu, development);
      }

      if (update) {
        await updateDoc(websiteDoc.ref, {
          development: development as any,
          changesToPublish: true,
        });
      }

      return { updated: update };
    },
    [applyChange],
  );

  const _saveGlobalSection = useCallback(
    async (websiteId: string, websiteUpdates: WebsiteUpdate[]) => {
      const globalSectionId = websiteId.replace('globalSectionId_', '');
      const globalSectionDoc = await getDoc(
        doc(
          db,
          'accounts',
          websiteUpdates[0].accountId,
          'globalSections',
          globalSectionId,
        ).withConverter(emptyConverter<GlobalSection>()),
      );

      const sectionDevelopment = globalSectionDoc.data()?.development;
      if (!globalSectionDoc.exists() || !sectionDevelopment)
        return { updated: false };

      let update = false;

      const simulatedDevelopment: WebsiteData = {
        sections: [sectionDevelopment],
      };

      for (const wu of websiteUpdates) {
        update = applyChange(wu, simulatedDevelopment);
      }

      if (update) {
        await updateDoc(globalSectionDoc.ref, {
          development: sectionDevelopment as any,
          changesToPublish: true,
        });
      }

      return { updated: update };
    },
    [applyChange],
  );

  useEffect(() => {
    const _updates = Object.values(updates);
    if (!_updates.length) return;

    const timer = setTimeout(async () => {
      const _updates = Object.values(updates);
      if (!_updates.length) return;

      setUpdates([]);

      const byDynamicPage = Object.entries(
        groupBy(_updates, (u) => u.campaignId),
      );

      for (const [dynamicPageId, campaignUpdates] of byDynamicPage) {
        const byWebsite = Object.entries(
          groupBy(campaignUpdates, (u) => u.websiteId),
        );
        const promises = byWebsite.map(async ([websiteId, websiteUpdates]) => {
          if (websiteId.startsWith('globalSectionId_')) {
            return _saveGlobalSection(websiteId, websiteUpdates);
          } else {
            return _saveWebsite(websiteId, websiteUpdates, dynamicPageId);
          }
        });

        await Promise.all(promises).finally(() => {
          setSaveStatus('done');
        });
      }
    }, 1500);

    return () => {
      clearTimeout(timer);
    };
  }, [_saveGlobalSection, _saveWebsite, updates]);

  const _discardChangesWebsite = useCallback(
    async (dynamicPageId: string, websiteId: string) => {
      if (!selectedAccount) return;
      if (discarding) return;

      if (updates.some((wu) => wu.websiteId === websiteId)) {
        return;
      }

      setDiscarding(true);
      try {
        const websiteDoc = await getDoc(
          doc(
            db,
            'accounts',
            selectedAccount.id,
            'campaigns',
            dynamicPageId,
            'websites',
            websiteId,
          ).withConverter(emptyConverter<Website>()),
        );

        const development = websiteDoc.data()?.development;
        const production = websiteDoc.data()?.production;
        if (!websiteDoc.exists() || !production) {
          throw new Error('This website was not yet published');
        }

        const discardWU = createWebsiteUpdate(
          dynamicPageId,
          websiteId,
          '',
          '',
          JSON.parse(JSON.stringify(production)),
          'discard',
          undefined,
          development,
        );

        if (discardWU) {
          setHistory((h) => {
            const newHistory = new LimitedStack(h);
            newHistory.push(discardWU);

            return newHistory;
          });
        }
        setRevertHistory(
          (revertHistory) => new LimitedStack(revertHistory.getMaxLength()),
        );

        await updateDoc(websiteDoc.ref, {
          development: production as any,
          changesToPublish: false,
        });

        sendMessageToIframeByWebsiteId(
          {
            eventName: 'websiteUpdated',
            website: production,
          },
          websiteId,
        );
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
        throw e;
      } finally {
        setDiscarding(false);
      }
    },
    [selectedAccount, discarding, updates, createWebsiteUpdate],
  );

  const _discardChangesGlobalSection = useCallback(
    async (dynamicPageId: string, websiteId: string) => {
      if (!selectedAccount) return;
      if (discarding) return;

      if (updates.some((wu) => wu.websiteId === websiteId)) {
        return;
      }

      setDiscarding(true);
      try {
        const globalSectionId = websiteId.replace('globalSectionId_', '');
        const globalSectionDoc = await getDoc(
          doc(
            db,
            'accounts',
            selectedAccount.id,
            'globalSections',
            globalSectionId,
          ).withConverter(emptyConverter<GlobalSection>()),
        );

        const development = globalSectionDoc.data()?.development;
        const production = globalSectionDoc.data()?.production;
        if (!globalSectionDoc.exists() || !production || !development) {
          throw new Error('This global section was not yet published');
        }

        const prodWebsiteData: WebsiteData = {
          sections: [production],
        };

        const developmentWebsiteData: WebsiteData = {
          sections: [development],
        };

        const discardWU = createWebsiteUpdate(
          dynamicPageId,
          websiteId,
          '',
          '',
          JSON.parse(JSON.stringify(prodWebsiteData)),
          'discard',
          undefined,
          developmentWebsiteData,
        );

        if (discardWU) {
          setHistory((h) => {
            const newHistory = new LimitedStack(h);
            newHistory.push(discardWU);

            return newHistory;
          });
        }
        setRevertHistory(
          (revertHistory) => new LimitedStack(revertHistory.getMaxLength()),
        );

        await updateDoc(globalSectionDoc.ref, {
          development: production as any,
          changesToPublish: false,
        });

        sendMessageToIframeByWebsiteId(
          {
            eventName: 'websiteUpdated',
            website: prodWebsiteData,
          },
          websiteId,
        );
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
        throw e;
      } finally {
        setDiscarding(false);
      }
    },
    [selectedAccount, discarding, updates, createWebsiteUpdate],
  );

  const discardChanges = useCallback(
    async (dynamicPageId: string, websiteId: string) => {
      if (websiteId.startsWith('globalSectionId_')) {
        return _discardChangesGlobalSection(dynamicPageId, websiteId);
      } else {
        return _discardChangesWebsite(dynamicPageId, websiteId);
      }
    },
    [_discardChangesGlobalSection, _discardChangesWebsite],
  );

  const _publishWebsite = useCallback(
    async (dynamicPageId: string, websiteId: string) => {
      if (!selectedAccount) return;
      if (publishing) return;

      if (updates.some((wu) => wu.websiteId === websiteId)) {
        return;
      }

      auditLog.log(
        'update',
        'Publishing website',
        'website',
        websiteId,
        undefined,
        undefined,
        {},
      );

      track('publish_website', {
        dynamicPageId,
        websiteId,
      });

      setPublishing(true);
      return publishWebsiteApi(selectedAccount.id, dynamicPageId, websiteId)
        .then(() => {})
        .catch((e) => {
          console.error(e);
          Sentry.captureException(e);
        })
        .finally(() => {
          setPublishing(false);
        });
    },
    [selectedAccount, publishing, updates, auditLog],
  );

  const _publishGlobalSection = useCallback(
    async (websiteId: string, globalSectionId: string) => {
      if (!selectedAccount) return;
      if (publishing) return;

      if (updates.some((wu) => wu.websiteId === websiteId)) {
        return;
      }

      auditLog.log(
        'update',
        'Publishing global section',
        'globalSection',
        globalSectionId,
        undefined,
        undefined,
        {},
      );

      track('publish_global_section', {
        globalSectionId,
        websiteId,
      });

      setPublishing(true);
      return publishGlobalSection(selectedAccount.id, globalSectionId).finally(
        () => {
          setPublishing(false);
        },
      );
    },
    [selectedAccount, publishing, updates, auditLog],
  );

  const publish = useCallback(
    async (dynamicPageId: string, websiteId: string) => {
      if (websiteId.startsWith('globalSectionId_')) {
        const globalSectionId = websiteId.replace('globalSectionId_', '');
        setPublishGlobalSectionModalOpen({
          websiteId,
          globalSectionId,
          loading: false,
        });
        return;
      } else {
        return _publishWebsite(dynamicPageId, websiteId);
      }
    },
    [_publishWebsite],
  );

  return (
    <WebsiteUpdaterContext.Provider
      value={{
        saveStatus,
        setSaveStatus,

        publishing,
        publish,

        discarding,
        discardChanges,

        createWebsiteUpdate,
        updateWebsite,

        canRevert: history.getSize() > 0,
        canForward: revertHistory.getSize() > 0,
        revertLatestUpdate,
        forwardRevert,
        clearHistory,

        applyChangesSync,
      }}
    >
      {props.children}
      <AlertModal
        open={publishGlobalSectionModalOpen !== null}
        onClose={() => setPublishGlobalSectionModalOpen(null)}
        title="Publish Global Section"
        description="Are you sure you want to publish this global section? This will update all websites that use this section."
        actions={[
          {
            title: 'Cancel',
            onClick: () => setPublishGlobalSectionModalOpen(null),
            type: 'secondary',
          },
          {
            title: 'Publish',
            type: 'primary',
            color: 'primary',
            loading: publishGlobalSectionModalOpen?.loading,
            onClick: () => {
              const { websiteId, globalSectionId } =
                publishGlobalSectionModalOpen || {};
              if (!websiteId || !globalSectionId) return;
              setPublishGlobalSectionModalOpen((prev) =>
                prev
                  ? {
                      ...prev,
                      loading: true,
                    }
                  : null,
              );
              _publishGlobalSection(websiteId, globalSectionId)
                .then(() => {
                  setPublishGlobalSectionModalOpen(null);
                })
                .catch((e) => {
                  Sentry.captureException(e);
                  console.error(e);
                  setPublishGlobalSectionModalOpen((prev) =>
                    prev
                      ? {
                          ...prev,
                          loading: false,
                        }
                      : null,
                  );
                });
            },
          },
        ]}
        type={'warn'}
      />
    </WebsiteUpdaterContext.Provider>
  );
}
